import { Stack } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Event } from '@feathr/blackbox';
import { CardV2 as Card, ConfirmModal, List, Text, toast, Toggle } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { useToggle } from '@feathr/hooks';

import { getVisibilityToggleCopy } from './ProjectVisibilityToggle.utils';

export interface IProjectVisibilityToggleProps {
  event: Event;
}

/**
 * Hide/restore a project via its `is_hidden` field: a card header whose toggle confirms before
 * hiding and restores immediately.
 */
function ProjectVisibilityToggle({ event }: Readonly<IProjectVisibilityToggleProps>): JSX.Element {
  const { t } = useTranslation();
  const { Events } = useStore();
  const [isModalOpen, toggleModal] = useToggle(false);

  const isHidden = !!event.get('is_hidden');
  const { confirmButtonText, errorMessage, helpText, label, modalBullets, modalIntro, modalTitle } =
    getVisibilityToggleCopy(isHidden, t);

  async function patchHidden(hidden: boolean): Promise<boolean> {
    try {
      await event.patch({ is_hidden: hidden });
      if (event.isErrored) {
        throw event.error;
      }
      Events.refreshApiCache();
      const { successMessage } = getVisibilityToggleCopy(hidden, t);
      toast(successMessage, { type: 'success' });
      return true;
    } catch {
      toast(errorMessage, { type: 'error' });
      return false;
    }
  }

  async function handleToggle(nextValue?: boolean): Promise<void> {
    if (nextValue) {
      toggleModal();
      return;
    }
    await patchHidden(false);
  }

  async function handleConfirmHide(): Promise<void> {
    const isSaved = await patchHidden(true);
    // Keep the modal open on failure so the user can retry.
    if (isSaved) {
      toggleModal();
    }
  }

  return (
    <>
      <Card.Header description={helpText} title={label}>
        <Toggle
          aria-label={label}
          disabled={event.isUpdating}
          onChange={handleToggle}
          value={isHidden}
        />
      </Card.Header>
      <ConfirmModal
        confirmButtonText={confirmButtonText}
        confirmButtonType={'danger'}
        confirmDisabled={event.isUpdating}
        onClose={toggleModal}
        onConfirm={handleConfirmHide}
        opened={isModalOpen}
        t={t}
        title={modalTitle}
      >
        <Stack gap={'xs'}>
          <Text truncate={false} variant={'defaultRegular'}>
            {modalIntro}
          </Text>
          <List hasBullets={true}>
            {modalBullets.map(function renderBullet(bullet): JSX.Element {
              return <List.Item key={bullet}>{bullet}</List.Item>;
            })}
          </List>
        </Stack>
      </ConfirmModal>
    </>
  );
}

export default observer(ProjectVisibilityToggle);
